import type { Subgroup } from '../../api/useSubgroups';

/**
 * Subgroup filter for the opener page. Subgroups are NESTED inside the
 * currently-selected group, so the selection only has meaning while a single
 * group is selected. It persists in the URL as `?group=<id>&subgroup=<id>`
 * (sentinel `unassigned` for the no-subgroup bucket; absent = All subgroups
 * within the group) alongside the `?group=` param the group filter owns.
 *
 * CRITICAL: the subgroup is meaningless outside its parent group, so the
 * subgroup param MUST be cleared whenever the group param changes — see
 * `writeGroupSelectionClearingSubgroup`. Pure helpers — EntryPage owns the
 * `useSearchParams` wiring.
 */

export const SUBGROUP_PARAM = 'subgroup';
export const UNASSIGNED_SUBGROUP_SENTINEL = 'unassigned';

export type SubgroupSelection =
  | { kind: 'all' }
  | { kind: 'unassigned' }
  | { kind: 'subgroup'; subgroupId: string };

export const ALL_SUBGROUPS: SubgroupSelection = { kind: 'all' };

export function readSubgroupSelection(
  params: URLSearchParams
): SubgroupSelection {
  const raw = params.get(SUBGROUP_PARAM);
  if (!raw) return ALL_SUBGROUPS;
  if (raw === UNASSIGNED_SUBGROUP_SENTINEL) return { kind: 'unassigned' };
  return { kind: 'subgroup', subgroupId: raw };
}

/** Returns a NEW URLSearchParams with the subgroup selection applied; every
 *  other param is preserved untouched. "All" deletes the param entirely. */
export function writeSubgroupSelection(
  params: URLSearchParams,
  selection: SubgroupSelection
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (selection.kind === 'all') next.delete(SUBGROUP_PARAM);
  else if (selection.kind === 'unassigned')
    next.set(SUBGROUP_PARAM, UNASSIGNED_SUBGROUP_SENTINEL);
  else next.set(SUBGROUP_PARAM, selection.subgroupId);
  return next;
}

/**
 * Dead-subgroup fallback: a URL pointing at a subgroup that no longer exists
 * (deleted, or the operator switched to a group that doesn't contain it)
 * resolves to All — but ONLY once the subgroups query for the active group has
 * successfully resolved. While it is still loading the selection is kept as-is
 * so the rail never flash-falls-back.
 */
export function resolveSubgroupSelection(
  selection: SubgroupSelection,
  subgroups: Pick<Subgroup, '_id'>[] | undefined,
  subgroupsResolved: boolean
): SubgroupSelection {
  if (selection.kind !== 'subgroup') return selection;
  if (!subgroupsResolved) return selection;
  const exists = (subgroups ?? []).some(s => s._id === selection.subgroupId);
  return exists ? selection : ALL_SUBGROUPS;
}

export function pairMatchesSubgroup(
  pair: { openerSubgroupId?: string | null },
  selection: SubgroupSelection
): boolean {
  if (selection.kind === 'all') return true;
  if (selection.kind === 'unassigned') return pair.openerSubgroupId == null;
  return pair.openerSubgroupId === selection.subgroupId;
}

/**
 * Stable identity key for a subgroup selection — drives effects that must
 * reset exactly when the operator switches the subgroup filter (e.g. clearing
 * the bulk-select set), regardless of object-reference churn.
 */
export function subgroupSelectionKey(selection: SubgroupSelection): string {
  return selection.kind === 'subgroup'
    ? `subgroup:${selection.subgroupId}`
    : selection.kind;
}
